import {
  createCHeliPlayerInfoFromChannelSessionDetail,
  type CHeliPlayerInfo
} from './CHeliPlayerInfo';

import type {IHeliPosChannelOptions} from './CHeliNetwork';
import type {IHeliNetError} from './IHeliNetError';
import type {PositionChannelDetail} from '../types/network';

export class CHeliPositionChannel implements IHeliNetError {
  #errorCode: string | null = null;
  #errorData: unknown = null;
  #detail: PositionChannelDetail | null;

  constructor(detail: PositionChannelDetail | null) {
    this.#detail = detail;
  }

  getErrorCode(): string | null {
    return this.#errorCode;
  }
  getErrorData(): unknown {
    return this.#errorData;
  }
  setError({code, data}: {code: string | null; data: unknown}) {
    this.#errorCode = code;
    this.#errorData = data;
  }
  isValid() {
    return this.#errorCode === null && this.#errorData === null;
  }

  getPositionChannelDetail(): PositionChannelDetail | null {
    return this.#detail;
  }

  getValue<T extends keyof PositionChannelDetail>(key: T) {
    if (this.#detail) {
      return this.#detail[key];
    }
    return null;
  }

  getChannelId(): string | null {
    return this.#detail?.channel_id ?? null;
  }

  /** 座標1対N音声か否か */
  isMultiVc(): boolean {
    return this.#detail?.allow_multi_voice_channel ?? false;
  }

  /** デフォルトボイスチャンネルを使うか否か */
  isEnableDefaultVc(): boolean {
    return this.#detail?.enable_default_voice_channel ?? false;
  }

  async update(params: IHeliPosChannelOptions): Promise<boolean> {
    if (!this.#detail?.channel_id) {
      this.setError({code: '400', data: 'channel_id is null'});
      return false;
    }
    try {
      const result = await globalThis.heliport.v3.api.channelSessionApi.updatePositionChannel(
        this.#detail.channel_id,
        params.channelType,
        params.name,
        params.description
      );

      if (result.status !== 'ok') {
        this.setError({
          code: 'errorCode' in result ? result.errorCode : result.code,
          data: result.data
        });
        return false;
      }

      this.#detail = result.data.position_channel as PositionChannelDetail;

      return true;
    } catch (error) {
      this.setError({code: '400', data: error});

      return false;
    }
  }

  async refresh() {
    if (!this.#detail?.channel_id) {
      this.setError({code: '400', data: 'channel_id is null'});
      return false;
    }
    try {
      const resPosChDetail =
        await globalThis.heliport.v3.api.channelSessionApi.fetchPositionChannelDetail(
          this.#detail.channel_id
        );
      if (resPosChDetail.status !== 'ok') {
        this.setError({
          code: 'errorCode' in resPosChDetail ? resPosChDetail.errorCode : resPosChDetail.code,
          data: resPosChDetail.data
        });
        return false;
      }
      const postionChannelDetail = resPosChDetail.data.position_channel as PositionChannelDetail;
      this.#detail = postionChannelDetail;
      return true;
    } catch (error) {
      this.setError({code: '400', data: error});
      return false;
    }
  }

  async getPlayerList(): Promise<CHeliPlayerInfo[]> {
    const updateResult = await this.refresh();
    if (updateResult && this.#detail && this.#detail.channel_sessions) {
      return this.#detail.channel_sessions.map(createCHeliPlayerInfoFromChannelSessionDetail);
    } else {
      return [];
    }
  }
}
